/**
 * ChatResponseDocumentList component for the Indigo design system.
 *
 * Displays a header followed by a vertical list of document rows inside a
 * chat response bubble. Each row reuses {@link ChatPromptFile} for the icon
 * + title + subtitle layout and may expose a small set of trailing actions
 * (e.g. open / download).
 *
 * @example
 * ```tsx
 * <ChatResponseDocumentList
 *   header="3 documents found"
 *   documents={[
 *     {
 *       id: "1",
 *       icon: <Icon name="pdf" size="m" />,
 *       title: "Full medical record.pdf",
 *       subtitle: "148 pages",
 *       onClick: () => openDoc("1"),
 *       actions: [
 *         { icon: <Icon name="link-out" size="s" />, label: "Open", onClick: ... },
 *         { icon: <Icon name="downloads" size="s" />, label: "Download", onClick: ... },
 *       ],
 *     },
 *   ]}
 * />
 * ```
 */
import { clsx } from "clsx";
import { forwardRef, ReactNode } from "react";
import { Button } from "../../Button/Button";
import { Body } from "../../Typography/Body";
import { ChatPromptFile } from "../ChatPromptFile";
import "./ChatResponseDocumentList.css";

export interface ChatResponseDocumentAction {
  /** Accessible label / tooltip for the action. */
  label: string;
  /** Icon node rendered inside the ghost button. */
  icon: ReactNode;
  /** Click handler. */
  onClick?: () => void;
  /** Optional test id for the action button. */
  "data-testid"?: string;
}

export interface ChatResponseDocumentItem {
  /** Stable identifier used as React key. */
  id: string;
  /** Icon rendered inside the 36×36 tile (typically `<Icon name="pdf" />`). */
  icon: ReactNode;
  /** Title text. */
  title: string;
  /** Optional subtitle text. */
  subtitle?: string;
  /** Click handler invoked when the row body is clicked. */
  onClick?: () => void;
  /** Optional trailing actions rendered on the right side of the row. */
  actions?: ChatResponseDocumentAction[];
  /** Optional test id for the row. */
  "data-testid"?: string;
}

export interface ChatResponseDocumentListProps {
  /** Optional header rendered above the document list. */
  header?: string;
  /** List of documents. */
  documents: ChatResponseDocumentItem[];
  /** Additional CSS class applied to the root. */
  className?: string;
  /** Test id for the root element. */
  "data-testid"?: string;
}

export const ChatResponseDocumentList = forwardRef<
  HTMLDivElement,
  ChatResponseDocumentListProps
>(({ header, documents, className, "data-testid": dataTestId }, ref) => {
  return (
    <div
      ref={ref}
      className={clsx("indigo-chat-response-document-list", className)}
      data-testid={dataTestId}
    >
      {header && (
        <Body
          as="span"
          size="xs"
          className="indigo-chat-response-document-list__header"
        >
          {header}
        </Body>
      )}
      <div className="indigo-chat-response-document-list__list" role="list">
        {documents.map((doc) => (
          <div
            key={doc.id}
            role="listitem"
            className="indigo-chat-response-document-list__row"
            data-testid={doc["data-testid"]}
          >
            <ChatPromptFile
              icon={doc.icon}
              title={doc.title}
              subtitle={doc.subtitle}
              onClick={doc.onClick}
              className="indigo-chat-response-document-list__file"
            />
            {doc.actions && doc.actions.length > 0 && (
              <div className="indigo-chat-response-document-list__actions">
                {doc.actions.map((action, index) => (
                  <Button
                    key={`${doc.id}-${index}`}
                    variant="ghost"
                    size="xs"
                    icon={action.icon}
                    onClick={action.onClick}
                    aria-label={action.label}
                    title={action.label}
                    data-testid={action["data-testid"]}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

ChatResponseDocumentList.displayName = "ChatResponseDocumentList";

export default ChatResponseDocumentList;
